/**
 * Safe wrappers around Web Storage (private mode / disabled storage never throws).
 *
 * Key conventions (all prefixed `kfms.`):
 *  - `kfms.lang`, `kfms.colorScheme`, `kfms.sidebarCollapsed` — device preferences (survive user switch)
 *  - `kfms.s.*` (sessionStorage) — per-user UI state; wiped on logout / user switch (SEC-10)
 */
export const storage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* ignore quota / privacy errors */
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
  getJson<T>(key: string, fallback: T): T {
    const raw = storage.get(key);
    if (!raw) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  },
  setJson(key: string, value: unknown): void {
    storage.set(key, JSON.stringify(value));
  },
};

/** Per-user session state (cleared on logout / switch user). */
export const sessionState = {
  PREFIX: 'kfms.s.',
  get<T>(key: string, fallback: T): T {
    try {
      const raw = window.sessionStorage.getItem(sessionState.PREFIX + key);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown): void {
    try {
      window.sessionStorage.setItem(sessionState.PREFIX + key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  },
  clearAll(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(sessionState.PREFIX)) keys.push(k);
      }
      keys.forEach((k) => window.sessionStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  },
};
